'use client'

import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { applyPortalAuthToken, bootstrapPortalAuthSession } from '@/lib/auth/azure-client'
import {
  clearStoredAuthSession,
  createEmptyAuthContext,
  normalizeAuthErrorMessage,
  OmiziAuthContext,
  OmiziAuthSession,
  OmiziPortalUser,
  readStoredAuthSession,
  subscribeToStoredAuthSessionChange,
} from '@/lib/auth/session'

export interface OmiziAuthContextValue {
  user: OmiziPortalUser | null
  authContext: OmiziAuthContext
  permissions: string[]
  userId: string | null
  tenantId: string | null
  businessId: string | null
  isLoaded: boolean
  isLoading: boolean
  isAuthenticated: boolean
  isAuthReady: boolean
  error: string | null
  getToken: () => Promise<string | null>
  refreshAuth: () => Promise<OmiziAuthSession | null>
}

export const OmiziAuthContextState = createContext<OmiziAuthContextValue | null>(null)

interface OmiziAuthProviderProps {
  children: ReactNode
}

function useOmiziAuthController(): OmiziAuthContextValue {
  const [session, setSession] = useState<OmiziAuthSession | null>(() => readStoredAuthSession())
  const [isLoaded, setIsLoaded] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refreshAuth = useCallback(async (): Promise<OmiziAuthSession | null> => {
    const storedSession = readStoredAuthSession()

    if (!storedSession?.accessToken) {
      applyPortalAuthToken(null)
      setSession(null)
      setError(null)
      setIsLoaded(true)
      setIsRefreshing(false)
      return null
    }

    setIsRefreshing(true)
    try {
      const hydrated = await bootstrapPortalAuthSession(storedSession.accessToken)
      setSession(hydrated)
      setError(null)
      setIsLoaded(true)
      return hydrated
    } catch (refreshError) {
      clearStoredAuthSession()
      applyPortalAuthToken(null)
      setSession(null)
      setError(normalizeAuthErrorMessage(refreshError, 'Unable to refresh Azure auth session'))
      setIsLoaded(true)
      return null
    } finally {
      setIsRefreshing(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      if (!cancelled) {
        await refreshAuth()
      }
    }

    void run()

    const unsubscribe = subscribeToStoredAuthSessionChange(() => {
      if (!cancelled) {
        void refreshAuth()
      }
    })

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [refreshAuth])

  const getToken = useCallback(async () => session?.accessToken ?? null, [session?.accessToken])

  return useMemo(() => {
    const authContext = session?.authContext ?? createEmptyAuthContext()
    const user = session?.user ?? null
    const permissions = session?.permissions ?? user?.permissions ?? authContext.permissions ?? []
    const userId = session?.authContext.subject || user?.id || null
    const tenantId = session?.user.tenantId || session?.authContext.omiziTenantId || null
    const businessId = session?.user.businessId || session?.authContext.omiziBusinessId || null
    const isAuthenticated = Boolean(session?.accessToken && userId)
    const isAuthReady = isLoaded && !isRefreshing
    const isLoading = !isLoaded || isRefreshing

    return {
      user,
      authContext,
      permissions,
      userId,
      tenantId,
      businessId,
      isLoaded,
      isLoading,
      isAuthenticated,
      isAuthReady,
      error,
      getToken,
      refreshAuth,
    }
  }, [error, getToken, isLoaded, isRefreshing, refreshAuth, session])
}

export function OmiziAuthProvider({ children }: OmiziAuthProviderProps) {
  const value = useOmiziAuthController()

  return <OmiziAuthContextState.Provider value={value}>{children}</OmiziAuthContextState.Provider>
}
